import React, { useEffect } from "react";
import {
  CaretRight,
  ShieldCheck,
  IdentificationCard,
  Airplane,
  Buildings,
  Car,
  Armchair,
  Check,
  Globe,
  Suitcase,
} from "@phosphor-icons/react";
import BrandMark from "./BrandMark";
import SegmentedSwitcher from "./SegmentedSwitcher";
import AccountButton from "../AccountButton";
import TipPanel from "./TipPanel";
import CurrencyPanel from "./CurrencyPanel";
import TranslatePanel from "./TranslatePanel";
import { VaultIllustration } from "./illustrations";
import WhatsNewBanner from "./WhatsNewBanner";

/* Inline colored sticker icons that match the brand-mark accents. */
function TranslateIcon({ active }) {
  const bg = active ? "rgba(255,255,255,0.25)" : "#EAF2FB";
  const fg = active ? "#fff" : "#2A6FD8";
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
      <circle cx="9" cy="9" r="9" fill={bg} />
      <text
        x="9"
        y="12"
        textAnchor="middle"
        fontSize="9"
        fontWeight="800"
        fill={fg}
        fontFamily="Plus Jakarta Sans, sans-serif"
      >
        文A
      </text>
    </svg>
  );
}
function CurrencyIcon({ active }) {
  const bg = active ? "rgba(255,255,255,0.25)" : "#DCEFE3";
  const fg = active ? "#fff" : "#2E7D5A";
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
      <circle cx="9" cy="9" r="9" fill={bg} />
      <text
        x="9"
        y="12.5"
        textAnchor="middle"
        fontSize="10"
        fontWeight="800"
        fill={fg}
        fontFamily="Plus Jakarta Sans, sans-serif"
      >
        $
      </text>
    </svg>
  );
}
function TipIcon({ active }) {
  const bg = active ? "rgba(255,255,255,0.25)" : "#FDE6D8";
  const fg = active ? "#fff" : "#E55B2C";
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
      <circle cx="9" cy="9" r="9" fill={bg} />
      <g fill={fg}>
        <rect x="5.4" y="4" width="1" height="4.5" rx="0.4" />
        <rect x="7" y="4" width="1" height="4.5" rx="0.4" />
        <rect x="8.6" y="4" width="1" height="4.5" rx="0.4" />
        <path d="M5 8.5 L9.8 8.5 L9.8 9.5 L7.9 9.5 L7.9 14.3 Q7.9 14.7 7.4 14.7 Q6.9 14.7 6.9 14.3 L6.9 9.5 L5 9.5 Z" />
        <path d="M11.6 4 Q13.7 4.6 13.9 8.4 Q13.9 9.7 12.5 9.7 L12.1 9.7 L12.1 14.3 Q12.1 14.7 11.6 14.7 Q11.1 14.7 11.1 14.3 L11.1 4.5 Q11.1 4 11.6 4 Z" />
      </g>
    </svg>
  );
}

const SEGMENTS = [
  { id: "translate", label: "Translate", renderIcon: TranslateIcon },
  { id: "currency", label: "Currency", renderIcon: CurrencyIcon },
  { id: "tip", label: "Tip", renderIcon: TipIcon },
];

/**
 * Home — the primary tab.
 *
 * Default view: Tip segment with header + segment switcher + tip content.
 * Tapping Currency or Translate (either from the switcher or a shortcut
 * card) pushes the user into a focused full-screen view with a back arrow.
 */
export default function Home({ openPaywall, onNavigate, pendingAction, consumeAction, view, setView }) {
  // `view` is now controlled by App.js (lifted state) so Settings can
  // offer a one-tap "Back to Translate" return path. Values:
  // "tip" | "currency" | "translate" | "translate:menu" | "phrases"

  // Reset to the Tip view whenever App.js signals a "reset-home" action
  // (fires when the user taps the Home dock icon while already on Home —
  // they expect it to drop them back to the main Tip screen).
  useEffect(() => {
    if (pendingAction === "reset-home") {
      setView("tip");
      consumeAction?.();
    }
  }, [pendingAction, consumeAction, setView]);

  // Auto-scan receipt prop forwarded down to the bill card when navigation
  // arrives with `pendingAction === "scan-receipt"` (e.g. from Saved tab).
  const autoScanReceipt = pendingAction === "scan-receipt";

  // The TipPanel emits semantic events like "translate", "translate:menu",
  // "currency", "phrases", "vault", "tsa". Tip-local views stay inside Home;
  // cross-tab navigations bubble up via onNavigate.
  function handleTipAction(target) {
    if (target === "vault" || target === "tsa") {
      onNavigate?.(target);
    } else {
      setView(target);
    }
  }

  if (view === "currency") {
    return <CurrencyPanel onBack={() => setView("tip")} />;
  }
  if (view === "translate" || view === "translate:menu" || view === "phrases") {
    return (
      <TranslatePanel
        onBack={() => setView("tip")}
        autoScanMenu={view === "translate:menu"}
        autoOpenPhrasebook={view === "phrases"}
      />
    );
  }

  return (
    <div className="px-5 pt-4 pb-2" data-testid="home-screen">
      {/* Top bar */}
      <div className="flex items-center justify-between mb-5">
        <BrandMark />
        <AccountButton openPaywall={() => openPaywall?.()} />
      </div>

      {/* Segmented switcher */}
      <SegmentedSwitcher value="tip" onChange={setView} items={SEGMENTS} />

      {/* What's new — one-time banner per release. Auto-hides after dismiss. */}
      <WhatsNewBanner />

      {/* Tip module */}
      <main className="mt-5 animate-fade-up">
        <TipPanel
          onNavigate={handleTipAction}
          onSaved={() => {
            // Stay on the Tip Calculator after save. The form is already
            // cleared by saveTip() and the toast confirms it. Users tap
            // the "Saved" tab manually to browse their log. Previously
            // this navigated to /vault which (with a PIN set) fired the
            // Document Vault lock screen and erased the user's tip
            // context — saved tips are NOT in the vault, that nav was a
            // stale leftover.
          }}
          autoScanReceipt={autoScanReceipt}
          onActionConsumed={consumeAction}
        />
      </main>

      {/* Travel Vault promo card */}
      <section
        data-testid="travel-vault-cta"
        className="mt-5 bg-sky-soft border border-navy/10 rounded-2xl p-3.5"
      >
        {/* Top row: illustration + title/description/CTA */}
        <div className="flex items-stretch gap-3">
          <div className="shrink-0 w-24 h-24 flex items-center justify-center">
            <VaultIllustration className="w-full h-full" />
          </div>
          <div className="flex-1 min-w-0 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-1.5">
                <h3 className="font-jakarta font-extrabold text-navy text-lg leading-tight">
                  Travel Vault
                </h3>
                <ShieldCheck size={18} weight="fill" color="#2E7D5A" />
              </div>
              <p className="text-navy-soft text-[11px] leading-snug mt-1 font-jakarta font-medium">
                Store travel essentials, memberships & trusted-traveler info in
                one secure place.
              </p>
            </div>
            <button
              type="button"
              data-testid="open-vault-btn"
              onClick={() => onNavigate?.("vault")}
              className="self-start mt-2 bg-navy text-white font-jakarta font-extrabold text-xs rounded-full pl-4 pr-3 py-2 flex items-center gap-1.5 shadow-soft active:scale-95 transition-transform"
            >
              Open Vault
              <CaretRight size={12} weight="bold" />
            </button>
          </div>
        </div>

        {/* Category pills — 4 + 3 grid */}
        <div className="mt-3 grid grid-cols-4 gap-1.5">
          {VAULT_PILLS.map((p) => (
            <button
              key={p.id}
              type="button"
              data-testid={`vault-pill-${p.id}`}
              onClick={() =>
                p.id === "lost"
                  ? onNavigate?.("lost")
                  : onNavigate?.(`vault:${p.id}`)
              }
              className={`bg-white rounded-xl border border-navy/10 px-1.5 py-2 flex flex-col items-center justify-center gap-1 active:scale-95 transition-transform shadow-sm ${
                p.fullRow ? "" : ""
              }`}
            >
              {p.iconSrc ? (
                <img
                  src={p.iconSrc}
                  alt=""
                  aria-hidden
                  style={{ width: 22, height: 22, objectFit: "contain" }}
                />
              ) : (
                <p.Icon size={18} weight="duotone" color={p.color} />
              )}
              <span className="font-jakarta font-bold text-navy text-[10px] leading-tight text-center">
                {p.label}
              </span>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}

const VAULT_PILLS = [
  { id: "photo_id", label: "Passport", Icon: IdentificationCard, iconSrc: "/assets/vault-icons/passport.png", color: "#0E2C54" },
  { id: "flight", label: "Flight", Icon: Airplane, iconSrc: "/assets/vault-icons/flight.png", color: "#2A6FD8" },
  { id: "hotel", label: "Hotel", Icon: Buildings, color: "#2E7D5A" },
  { id: "car", label: "Car rental", Icon: Car, iconSrc: "/assets/vault-icons/car.png", color: "#E55B2C" },
  { id: "lounge", label: "Lounge", Icon: Armchair, iconSrc: "/assets/vault-icons/lounge.png", color: "#0E2C54" },
  { id: "tsaprecheck", label: "TSA PreCheck®", Icon: Check, iconSrc: "/assets/vault-icons/tsa.png", color: "#2E7D5A" },
  { id: "globalentry", label: "Global Entry", Icon: Globe, iconSrc: "/assets/vault-icons/globalentry.png", color: "#2A6FD8" },
  { id: "lost", label: "Lost?", Icon: Suitcase, iconSrc: "/assets/lost-luggage-icon.png", color: "#E55B2C" },
];
